// src/gac/RegenerateImagesPanel.js
// Extracted from GlobalActionCenter.js (GAC modularization, phase 1). No behaviour change.
import React from 'react';
import axios from '../axiosInstance';
import '../GlobalActionCenterStyles.css';

const RegenerateImagesPanel = ({
    globalCompany,
    globalStores,
    executing,
    setExecuting,
    results,
    setResults,
    setErrors,
}) => {
    // Execute image regeneration across all stores of the selected company.
    // Manages its own executing state because the job completes via polling,
    // not when the start request returns.
    const executeRegenerateImages = async () => {
        if (!globalCompany) {
            alert('Please select a company first');
            return;
        }

        const storeCount = globalStores.length;
        const confirmMsg = storeCount > 0
            ? `Regenerate ALL label images for ${globalCompany.companyName} (${globalCompany.companyCode}) across ${storeCount} store(s)?\n\nSaaS will re-render and re-push every label image in each store.`
            : `Regenerate ALL label images for ${globalCompany.companyName} (${globalCompany.companyCode})?\n\nAll stores of the company will be processed.`;
        if (!window.confirm(confirmMsg)) {
            return;
        }

        setExecuting(true);
        setResults(null);
        setErrors([]);

        try {
            const startResponse = await axios.post('global-action/actions/regenerate-images-async', {
                companyCode: globalCompany.companyCode
            });

            if (!startResponse.data.success) {
                setErrors(['Failed to start regeneration: ' + (startResponse.data.message || 'Unknown error')]);
                setExecuting(false);
                return;
            }

            const jobId = startResponse.data.jobId;
            console.log('Regenerate images job started:', jobId);

            const pollInterval = setInterval(async () => {
                try {
                    const statusResponse = await axios.get(`/global-action/actions/job-status/${jobId}`);

                    if (statusResponse.data.success) {
                        const job = statusResponse.data.job;
                        setResults({ ...job.results, progress: job.progress });

                        if (job.status === 'completed' || job.status === 'failed') {
                            clearInterval(pollInterval);
                            setExecuting(false);

                            if (job.status === 'failed') {
                                setErrors(['Job failed: ' + (job.error || 'Unknown error')]);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error polling job status:', error);
                    clearInterval(pollInterval);
                    setExecuting(false);
                    setErrors(['Error checking job status: ' + error.message]);
                }
            }, 2000);
        } catch (error) {
            setErrors(['Regenerate images failed: ' + (error.response?.data?.message || error.message)]);
            setExecuting(false);
        }
    };

    return (
        <>
            <div className="action-config-section">
                <h3>Regenerate Images Configuration</h3>
                <div className="config-panel">
                    {!globalCompany ? (
                        <p className="config-hint">Select a company above to begin.</p>
                    ) : (
                        <>
                            <div className="config-row">
                                <label>Company:</label>
                                <span>{globalCompany.companyName} ({globalCompany.companyCode})</span>
                            </div>
                            <div className="config-row">
                                <label>Stores:</label>
                                <span>{globalStores.length > 0 ? `${globalStores.length} store(s) will be processed` : 'All stores of the company'}</span>
                            </div>
                            <div className="config-row">
                                <label>What this does:</label>
                                <span>
                                    Asks SOLUM SaaS to re-render and re-push every label image, store by store
                                    (regenerateType=ALL). After each store is fired, its label statuses are
                                    snapshotted as verification. SaaS only pushes to tags whose rendered image
                                    actually changed.
                                </span>
                            </div>

                            {executing && results?.progress && (
                                <div className="config-row">
                                    <label>Progress:</label>
                                    <span>
                                        Store {results.progress.storesProcessed} of {results.progress.totalStores}
                                    </span>
                                </div>
                            )}

                            <div className="action-buttons">
                                <button
                                    className="execute-button"
                                    onClick={executeRegenerateImages}
                                    disabled={executing}
                                >
                                    {executing ? 'Regenerating...' : 'Regenerate All Images'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default RegenerateImagesPanel;

// Rendered by the shell AFTER the errors section, exactly where the results block used to sit.
export const RegenerateImagesResults = ({ results }) => (
    <>
        {results && results.summary && (
            <div className="results-section">
                <h3>Regeneration Results</h3>

                <div className="results-summary">
                    <div className="summary-card">
                        <div className="summary-label">Stores Processed</div>
                        <div className="summary-value">{results.summary.total_stores_processed}</div>
                    </div>
                    <div className="summary-card success">
                        <div className="summary-label">Stores Accepted</div>
                        <div className="summary-value">{results.summary.total_stores_accepted}</div>
                    </div>
                    <div className="summary-card error">
                        <div className="summary-label">Stores Failed</div>
                        <div className="summary-value">{results.summary.total_stores_failed}</div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-label">Labels Seen</div>
                        <div className="summary-value">{results.summary.total_labels_seen}</div>
                    </div>
                    <div className="summary-card highlight">
                        <div className="summary-label">Success Rate</div>
                        <div className="summary-value">{results.summary.success_rate}%</div>
                    </div>
                </div>

                {/* Aggregate label status counts (verification snapshot) */}
                {results.summary.label_status_counts && Object.keys(results.summary.label_status_counts).length > 0 && (
                    <div className="store-results">
                        <h4>Label Status Snapshot (verification)</h4>
                        <div className="results-summary">
                            {Object.entries(results.summary.label_status_counts).map(([status, count]) => (
                                <div key={status} className={`summary-card ${status === 'SUCCESS' ? 'success' : status === 'TIMEOUT' ? 'error' : ''}`}>
                                    <div className="summary-label">{status}</div>
                                    <div className="summary-value">{count}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Per-Store Results */}
                {results.stores && results.stores.length > 0 && (
                    <div className="store-results">
                        <h4>Store Results ({results.stores.length} stores)</h4>
                        <div className="results-table">
                            <div className="results-header">
                                <div className="col-store">Store</div>
                                <div className="col-labels">Labels</div>
                                <div className="col-flipped">Accepted</div>
                                <div className="col-failed">Statuses</div>
                                <div className="col-status">Result</div>
                            </div>
                            {results.stores.map((store, index) => (
                                <div key={index} className={`results-row ${store.status}`}>
                                    <div className="col-store">{store.store_code}</div>
                                    <div className="col-labels">{store.labels_total}</div>
                                    <div className="col-flipped">{store.accepted ? 'Yes' : 'No'}</div>
                                    <div className="col-failed">
                                        {Object.entries(store.status_counts || {})
                                            .map(([st, ct]) => `${st}: ${ct}`)
                                            .join(', ') || '-'}
                                    </div>
                                    <div className="col-status">
                                        {store.status === 'success' && 'Success'}
                                        {store.status === 'failed' && 'Failed'}
                                        {store.status === 'pending' && 'Pending'}
                                    </div>
                                    {store.errors && store.errors.length > 0 && (
                                        <div className="col-errors">
                                            {store.errors.map((err, i) => (
                                                <div key={i} className="error-detail">{err}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )}
    </>
);
